import { Button, Stack, Typography, useTheme } from "@mui/material";
import { FileText, Plus } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PortfolioItemIcon } from "web_myplann/Portfolio/Components/PortfolioItemIcon";
import { PinCandidateRow } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/PinCandidateRow";
import type {
	PinCandidate,
	PinKind,
} from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/pinKind";
import { usePinCandidateRows } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/usePinCandidateRows";
import { DocumentRowSkeleton } from "web_myplann/Portfolio/Components/Redesign/Documents/DocumentRowSkeleton";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";

const SKELETON_ROWS = 5;

type Props = {
	kind: PinKind;
	open: boolean;
	onClose: () => void;
	candidates: PinCandidate[];
	isLoading: boolean;
	isPending: boolean;
	onSelect: (candidate: PinCandidate) => void;
};

export const PinExistingDrawer: FC<Props> = ({
	kind,
	open,
	onClose,
	candidates,
	isLoading,
	isPending,
	onSelect,
}) => {
	const theme = useTheme();
	const { palette, iconSize } = theme;
	const { t } = useResource();
	const navigate = useNavigate();
	const kindResources = Resources.PortfolioDetail.Pin.Kind[kind];

	const rows = usePinCandidateRows(candidates);

	return (
		<MyPlannDrawer
			anchor="bottom"
			grabber
			open={open}
			onClose={onClose}
			title={t(kindResources.selectTitle)}
		>
			<Stack pb={theme.spacingTokens.xl}>
				{isLoading ? (
					Array.from({ length: SKELETON_ROWS }).map((_, index) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton list
						<DocumentRowSkeleton key={index} />
					))
				) : rows.length === 0 ? (
					<>
						<Stack
							px={theme.spacingTokens.lg}
							pt={theme.spacingTokens.md}
							pb={theme.spacingTokens.sm}
							gap={theme.spacingTokens.xs}
						>
							<Typography variant="body2">
								{t(Resources.PortfolioDetail.Pin.selectEmpty)}
							</Typography>
							<Typography variant="body2" color={palette.grey[500]}>
								{t(Resources.PortfolioDetail.Pin.selectEmptyHint)}
							</Typography>
						</Stack>
						<Button
							variant="inlineAction"
							startIcon={<Plus size={iconSize.sm} />}
							onClick={() =>
								navigate(Routing.Portfolio.PortfolioAddItem.path())
							}
							sx={{
								borderTop: theme.colors.border.solid(
									theme.palette.surface.border,
								),
							}}
						>
							{t(Resources.PortfolioDetail.Pin.selectEmptyAddItem)}
						</Button>
					</>
				) : (
					rows.map((row, index) => (
						<PinCandidateRow
							key={row.key}
							row={row}
							icon={
								row.candidate.kind === "document" ? (
									<FileText size={iconSize.md} color={palette.grey[500]} />
								) : (
									<PortfolioItemIcon
										domain={row.candidate.data.domain}
										propertyType={row.candidate.data.type}
									/>
								)
							}
							iconBg={
								row.candidate.kind === "document"
									? palette.grey[100]
									: undefined
							}
							isLast={index === rows.length - 1}
							disabled={isPending}
							onClick={() => onSelect(row.candidate)}
						/>
					))
				)}
			</Stack>
		</MyPlannDrawer>
	);
};
